import { FC, Fragment, useCallback, useEffect, useState, useRef } from 'react'
import CustomToolTip from './CustomToolTip'

import * as Cesium from "cesium";
import { getMapSettingMenus, getMapStyleId, getZoomInMoveRate, MAX_ZOOM_OUT_HEIGHT, FIXED_MOVE_RATE } from '../../utils/cesiumUtil'
import { AntSwitch } from './AntSwitch';
import './ViewerSettings.css'
import { createNaturalEarthProvider, offline } from '../../utils/commonFunctions';
import LayersFilter from './LayersFilter';
import { VIEW_FEATURES, VIEW_TYPES } from '../../utils/constants';

interface ViewerSettingsProps {
    viewer: Cesium.Viewer | null
    viewerId: string | undefined | null
    viewType: string
    className?: string
    disabled?: boolean
}

const ViewerSettings: FC<ViewerSettingsProps> = ({ viewer, viewerId, viewType, className, disabled = false }) => {

    const [isEclipseOn, setIsEclipseOn] = useState<any>('Eclipse')
    const [openMenuOptions, setOpenMenuOptions] = useState<any>(false)
    const [openMenuDetails, setOpenMenuDetails] = useState<any>(false)
    const [selectedMenuDetailsOption, setSelectedMenuDetailsOption] = useState<any>(null)
    const viewConfig = VIEW_FEATURES[viewType]
    const enableLayersFilter = viewConfig.showLayersFilter
    const enableGrid = viewConfig.showGrid

    const [gridVisible, setGridVisible] = useState(false);
    const gridPrimitiveRef = useRef<Cesium.Primitive | null>(null);
    const labelsRef = useRef<Cesium.LabelCollection | null>(null);

    const ORBIT_MENU_ITEMS = getMapSettingMenus(openMenuDetails, isEclipseOn, enableLayersFilter, enableGrid, gridVisible)

    const update_base_map_layer = (baseMapOption) => {
        handleFitToscreenMode();
        const imaginary = new Cesium.MapboxStyleImageryProvider({
            styleId: getMapStyleId(baseMapOption),
            accessToken: process.env.REACT_APP_MAPBOX_TOKEN!
        });
        viewer?.imageryLayers.addImageryProvider(offline() ? createNaturalEarthProvider() : imaginary)
    }

    const update_view_mode = (mode) => {
        if (viewer !== null) {
            handleFitToscreenMode();
            mode === "2D" ? viewer.scene.morphTo2D(0) : viewer.scene.morphTo3D(0)
        }
    }

    const update_map_style = useCallback((data) => {
        if (!viewerId) return
        const previous_map_details = JSON.parse(sessionStorage.getItem('map_details') || '{}')
        let map_details = {
            ...previous_map_details,
            [viewerId]: {
                ...previous_map_details?.[viewerId],
                'Base Map': data?.['type'] === 'Base Map' ? data?.['Base Map'] : previous_map_details?.[viewerId]?.['Base Map'],
                'View': data?.['type'] === 'View' ? data?.['View'] : previous_map_details?.[viewerId]?.['View'],
                'manual3DMode': data?.['type'] === 'View' ? data?.['View'] === '3D' ? true : false : previous_map_details?.[viewerId]?.['manual3DMode']
            }
        }
        sessionStorage.setItem('map_details', JSON.stringify(map_details))

        if (data?.['type'] === 'Base Map') {
            setSelectedMenuDetailsOption({
                ...selectedMenuDetailsOption,
                "Base Map": data?.['Base Map']
            });
            update_base_map_layer(data?.['Base Map']);
        } else if (data?.['type'] === 'View') {
            setSelectedMenuDetailsOption({
                ...selectedMenuDetailsOption,
                "View": data?.['View']
            });
            update_view_mode(data?.['View']);
        }
    }, [selectedMenuDetailsOption]);

    useEffect(() => {
        if (viewerId && viewer) {
            const map_details = JSON.parse(sessionStorage.getItem('map_details') || '{}')
            const defaultEclipseState = viewConfig?.showEclipse ? "Eclipse" : false;
            if (map_details && map_details[viewerId]) {
                setSelectedMenuDetailsOption({
                    'Base Map': map_details[viewerId]['Base Map'],
                    'View': map_details[viewerId]['View']
                })
                update_base_map_layer(map_details[viewerId]['Base Map'])

                const storedEclipseState = map_details[viewerId]["Eclipse"]
                let finalEclipseState = storedEclipseState
                if (!viewConfig?.showEclipse && storedEclipseState === "Eclipse") {
                    finalEclipseState = false
                } else if (viewConfig?.showEclipse && !storedEclipseState) {
                    finalEclipseState = defaultEclipseState
                }

                finalEclipseState === "Eclipse" ? viewer.scene.globe.enableLighting = true : viewer.scene.globe.enableLighting = false

                const updatedMapDetails = {
                    ...map_details,
                    [viewerId]: {
                        ...map_details[viewerId],
                        Eclipse: finalEclipseState,
                    },
                }
                sessionStorage.setItem("map_details", JSON.stringify(updatedMapDetails))
                update_view_mode(map_details[viewerId]["View"]);
            } else {
                viewer.scene.globe.enableLighting = viewConfig?.enableLighting || false;
                setSelectedMenuDetailsOption({
                    "View": '2D',
                    "Base Map": 'Mapbox Dark'
                })
                let details = {
                    ...map_details,
                    [viewerId]: {
                        "View": '2D',
                        "Base Map": 'Mapbox Dark',
                        Eclipse: defaultEclipseState,
                    }
                }
                if (!viewConfig?.showEclipse) setIsEclipseOn(false);
                sessionStorage.setItem('map_details', JSON.stringify(details));
            }
        }
    }, [viewerId, viewer, viewType])


    const changeEclipseMode = () => {
        if (!viewer) return
        viewer.scene.globe.enableLighting = !viewer.scene.globe.enableLighting;
        viewer.camera.zoomOut(0);
    }

    const toggleCesiumGridView = (
        viewer: Cesium.Viewer | null,
        gridPrimitiveRef: React.MutableRefObject<Cesium.Primitive | null>,
        labelsRef: React.MutableRefObject<Cesium.LabelCollection | null>,
        setGridVisible: (val: boolean) => void
    ) => {
        if (!viewer) return;

        const ellipsoid = Cesium.Ellipsoid.WGS84;
        const baseColor = Cesium.Color.fromCssColorString("#c0c0c0");
        const color = new Cesium.Color(baseColor.red, baseColor.green, baseColor.blue, 0.25);

        if (!gridPrimitiveRef.current && !labelsRef.current) {
            const labels = new Cesium.LabelCollection();
            const instances: Cesium.GeometryInstance[] = [];

            for (let lat = -80; lat <= 80; lat += 10) {
                const latPositions: Cesium.Cartesian3[] = [];
                for (let lon = -180; lon <= 180; lon += 5) {
                    latPositions.push(ellipsoid.cartographicToCartesian(Cesium.Cartographic.fromDegrees(lon, lat)));
                }

                const geometry = new Cesium.PolylineGeometry({
                    positions: latPositions,
                    width: 1.0,
                    vertexFormat: Cesium.PolylineColorAppearance.VERTEX_FORMAT,
                });

                instances.push(
                    new Cesium.GeometryInstance({
                        geometry,
                        attributes: {
                            color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
                        },
                    })
                );

                if (lat !== 0) {
                    labels.add({
                        position: ellipsoid.cartographicToCartesian(Cesium.Cartographic.fromDegrees(0.1, lat)),
                        text: `${lat}°`,
                        font: "12px Montserrat, sans-serif",
                        fillColor: Cesium.Color.fromCssColorString("#F0F0F0"),
                        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                        outlineWidth: 1,
                        outlineColor: Cesium.Color.BLACK,
                        horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
                        verticalOrigin: Cesium.VerticalOrigin.CENTER,
                    });
                }
            }

            for (let lon = -180; lon <= 180; lon += 10) {
                const lonPositions: Cesium.Cartesian3[] = [];
                for (let lat = -90; lat <= 90; lat += 5) {
                    lonPositions.push(ellipsoid.cartographicToCartesian(Cesium.Cartographic.fromDegrees(lon, lat)));
                }

                const geometry = new Cesium.PolylineGeometry({
                    positions: lonPositions,
                    width: 1.0,
                    vertexFormat: Cesium.PolylineColorAppearance.VERTEX_FORMAT,
                });

                instances.push(
                    new Cesium.GeometryInstance({
                        geometry,
                        attributes: {
                            color: Cesium.ColorGeometryInstanceAttribute.fromColor(color),
                        },
                    })
                );

                if (lon !== 0) {
                    labels.add({
                        position: ellipsoid.cartographicToCartesian(Cesium.Cartographic.fromDegrees(lon, 0.1)),
                        text: `${lon}°`,
                        font: "12px Montserrat, sans-serif",
                        fillColor: Cesium.Color.fromCssColorString("#F0F0F0"),
                        style: Cesium.LabelStyle.FILL_AND_OUTLINE,
                        outlineWidth: 1,
                        outlineColor: Cesium.Color.BLACK,
                        horizontalOrigin: Cesium.HorizontalOrigin.LEFT,
                        verticalOrigin: Cesium.VerticalOrigin.CENTER,
                    });
                }
            }

            const gridPrimitive = new Cesium.Primitive({
                geometryInstances: instances,
                appearance: new Cesium.PolylineColorAppearance(),
                show: true,
            });

            viewer.scene.primitives.add(gridPrimitive);
            viewer.scene.primitives.add(labels);

            gridPrimitiveRef.current = gridPrimitive;
            labelsRef.current = labels;
            setGridVisible(true);
        } else {
            if (gridPrimitiveRef.current) {
                gridPrimitiveRef.current.show = !gridPrimitiveRef.current.show;
            }
            if (labelsRef.current) {
                labelsRef.current.show = !labelsRef.current.show;
            }

            setGridVisible(gridPrimitiveRef.current?.show ?? false);
        }
    };


    const zoomIn = () => {
        if (viewer) {
            if (viewer.camera.positionCartographic.height) {
                const moveRate = getZoomInMoveRate(viewer);
                viewer.camera.moveForward(moveRate);
            }
        }

    };

    const zoomOut = () => {
        if (viewer) {
            const cameraHeight = viewer.scene.globe.ellipsoid.cartesianToCartographic(viewer.camera.position).height;
            if (cameraHeight < MAX_ZOOM_OUT_HEIGHT) {
                viewer.camera.moveBackward(FIXED_MOVE_RATE);
            }
        }
    };


    const handleFitToscreenMode = () => {
        if (viewer) {
            setTimeout(() => {
                viewer.camera.setView({
                    destination: Cesium.Cartesian3.fromDegrees(0, 0, 32000000)
                });
            }, 100)
        }
    }

    const update_eclipse_mode = (data) => {
        if (!viewerId) return
        const previous_map_details = JSON.parse(sessionStorage.getItem('map_details') || '{}');
        let map_details = {
            ...previous_map_details,
            [viewerId]: {
                ...previous_map_details?.[viewerId],
                'Eclipse': data,
            }
        }
        sessionStorage.setItem('map_details', JSON.stringify(map_details));
        viewer?.scene.requestRender();
    }

    const close_menu = () => {
        setOpenMenuOptions(false);
        setOpenMenuDetails(false);
    }

    useEffect(() => {
        document.addEventListener('click', close_menu)
        return () => {
            document.removeEventListener('click', close_menu)
        }
    }, [])

    return (
        <Fragment>
            {(viewerId && viewerId !== undefined) &&
                <div className={`settingOrbitLayer 
                        ${className || ''} ${viewType === VIEW_TYPES.UCP ? 'settingsFeatureBar' : ''} 
                        ${openMenuOptions ? 'active' : ''}
                        ${disabled ? 'disabled__button' : ''}
                    `}
                    onClick={() => {
                        if (!openMenuDetails) {
                            close_menu()
                        }
                    }}
                >
                    <div className="settingOrbitLayerContainer">
                        {Object.keys(ORBIT_MENU_ITEMS)?.map(item => (
                            <button title={item} className={`SettingOrbitButtonStyle`}
                                key={item}
                            >
                                <CustomToolTip title={item} placement='left'>
                                    <div
                                        style={{ padding: '10px', display: 'inline-block' }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (item === "Zoom In") {
                                                zoomIn();
                                                setOpenMenuDetails(false)
                                            }
                                            else if (item === "Zoom Out") {
                                                zoomOut()
                                                setOpenMenuDetails(false)
                                            }
                                            else if (item === "Reset View") {
                                                handleFitToscreenMode()
                                                setOpenMenuDetails(false)
                                            }
                                            else if (item === "Layers Filter") {
                                                setOpenMenuDetails(openMenuDetails === item ? false : item)
                                            }
                                            else if (item === "Eclipse") {
                                                changeEclipseMode()
                                                setIsEclipseOn(isEclipseOn === 'Eclipse' ? false : 'Eclipse')
                                                update_eclipse_mode(isEclipseOn === 'Eclipse' ? false : 'Eclipse')
                                            }
                                            else if (item === "Grid") {
                                                toggleCesiumGridView(viewer, gridPrimitiveRef, labelsRef, setGridVisible);
                                                setOpenMenuDetails(openMenuDetails === item ? false : item)
                                            }
                                            else {
                                                setOpenMenuDetails(openMenuDetails === item ? false : item)
                                            }
                                        }}
                                    >
                                        {ORBIT_MENU_ITEMS[item]?.['svg']}
                                    </div>
                                </CustomToolTip>
                            </button>
                        ))}
                    </div>

                    <button className={`SettingOrbitButtonHandler`}>
                        <CustomToolTip title='Settings' placement='left'>
                            <div
                                style={{ padding: '10px', display: 'inline-block' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuOptions(!openMenuOptions);
                                    setOpenMenuDetails(false);
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M2.50488 0H1.50488V5H2.50488V0ZM8.60488 5H5.40488L5.00488 4.45V3.45L5.40488 3H8.60488L9.00488 3.5V4.5L8.60488 5ZM3.60488 8H0.404883L0.00488281 7.5V6.5L0.404883 6H3.60488L4.00488 6.5V7.5L3.60488 8ZM7.50488 0H6.50488V2H7.50488V0ZM6.50488 6H7.50488V12H6.50488V6ZM2.50488 9H1.50488V12H2.50488V9ZM10.4049 9H13.5949L13.9949 8.5V7.55L13.5949 7.05H10.4049L10.0049 7.55V8.5L10.4049 9ZM12.5049 0H11.5049V6H12.5049V0ZM11.5049 10H12.5049V12H11.5049V10Z" fill={openMenuOptions ? "#CCF54E" : '#F0F0F0'} fillOpacity={openMenuOptions ? "1" : "0.6"} />
                                </svg>
                            </div>
                        </CustomToolTip>

                    </button>
                    <div className={`settingOrbitDetailsLayer ${openMenuDetails === 'Map Style' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <div className="d-flex align-items-center gap-2 px-3">
                            {openMenuDetails === 'Map Style' && ORBIT_MENU_ITEMS[openMenuDetails]?.['svg']}
                            <span className="fs-20 color-atmos">{openMenuDetails}</span>
                        </div>
                        <div className={`d-flex align-items-center gap-3 cursor-pointer px-4`}>
                            <span style={{ color: selectedMenuDetailsOption?.['View'] === '2D' ? "#CCF54E" : "gray", }}> 2D </span>
                            <AntSwitch
                                name={selectedMenuDetailsOption?.['View']}
                                checked={selectedMenuDetailsOption?.['View'] === '3D'}
                                color="success"
                                onKeyDown={(e) => {
                                    if (e.keyCode === 13) {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    let data = {
                                        type: 'View',
                                        "View": selectedMenuDetailsOption?.['View'] === '2D' ? '3D' : '2D'
                                    }
                                    update_map_style(data)
                                }}
                                inputProps={{ "aria-label": "ant design" }}
                            />
                            <span style={{ color: selectedMenuDetailsOption?.['View'] === '3D' ? "#CCF54E" : "gray", }} > 3D </span>
                        </div>

                        {!offline() && <div className="d-flex flex-column gap-3 px-4">
                            <span className="fs-18 opacity-75">Base Map</span>
                            <div className="d-flex flex-column gap-3 px-2">
                                {openMenuDetails === 'Map Style' && ORBIT_MENU_ITEMS[openMenuDetails]?.['Base Map'].map(item => (
                                    <div key={item} className={`d-flex align-items-center gap-3 cursor-pointer ${selectedMenuDetailsOption?.['Base Map'] === item ? '' : 'opacity-50'}`}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            let data = {
                                                type: 'Base Map',
                                                "Base Map": item
                                            }
                                            update_map_style(data)
                                        }}
                                    >
                                        {selectedMenuDetailsOption?.['Base Map'] === item && <img src={require('../../assets/images/svgs/roundChecked.svg').default} height={20} width={20} alt='unchecked' />}
                                        {selectedMenuDetailsOption?.['Base Map'] !== item && <img src={require('../../assets/images/svgs/roundUnChecked.svg').default} height={20} width={20} alt='unchecked' />}
                                        <span className={`fs-16`}>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>}
                    </div>
                    {viewConfig.showLayersFilter && (<LayersFilter viewer={viewer} isActive={openMenuDetails === "Layers Filter"} />)}
                </div>
            }
        </Fragment>
    )
}

export default ViewerSettings